/**
 * Sort configuration for LocatesV2Widget.
 *
 * This module defines available sorts with their URL parameters and comparators.
 * Each sort has both ascending (up) and descending (down) configurations.
 *
 * Backend whitelisted fields: '_id', 'locateRequest_symbol', 'result_locate_state', 'createdAt'
 */
import type { Instant } from '@js-joda/core';
import type { LocateAccount } from '@thinkalpha/ui-types/contracts/locates/platforms';
import type { FlattenedLocateResultRow } from '@thinkalpha/ui-types/types/labModern';

/**
 * Available sort options for the LocatesV2Widget.
 * These correspond to the user-selectable sorts in the UI.
 *
 * Note: All sorts automatically include result_state desc as the first sort field
 * to ensure pending items always appear at the top, regardless of secondary sort direction.
 */
export type LocatesV2Sort =
    | 'createdAt desc'
    | 'createdAt asc'
    | 'symbol desc'
    | 'symbol asc'
    | 'lastUpdateTime desc'
    | 'lastUpdateTime asc'
    | 'price desc'
    | 'price asc'
    | 'quantity desc'
    | 'quantity asc'
    | 'account desc'
    | 'account asc'
    | 'borrowStatus desc'
    | 'borrowStatus asc'
    | 'provider desc'
    | 'provider asc'
    | 'totalCost desc'
    | 'totalCost asc';

/**
 * Default sort for the LocatesV2Widget.
 * Sorts by result_state desc (pending items first), then createdAt desc (newest first).
 */
export const DEFAULT_LOCATES_V2_SORT: LocatesV2Sort = 'createdAt desc';

export interface SortConfig {
    downComparator: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => number;
    downSort: string;
    upComparator: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => number;
    upSort: string;
}

export interface SortDictionary {
    account: SortConfig;
    borrowStatus: SortConfig;
    createdAt: SortConfig;
    lastUpdateTime: SortConfig;
    price: SortConfig;
    provider: SortConfig;
    quantity: SortConfig;
    symbol: SortConfig;
    totalCost: SortConfig;
}

type FieldComparator = (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => number;

/**
 * Priority map for locates order state categories, matching backend sort logic.
 * Higher priority values sort first (descending order).
 */
const resultStatePriorityMap: Record<string, number> = {
    progress: 250,
    actionable: 200,
    accepted: 125,
    final: 100,
    rejected: 75,
    initial: 0,
};

/**
 * Maps a ResultState enum value to its backend category.
 * This allows the priority map to use category names while the data contains specific state values.
 */
export function getStateCategory(state: string): string {
    const normalized = state.toLowerCase();
    switch (normalized) {
        // Actionable - needs user attention (displayed as "Offered" or "Quoted")
        case 'actionable':
        case 'provided':
        case 'quoted':
            return 'actionable';

        // Accepted - successful final states
        case 'accepted':
            return 'accepted';

        // Injected - other final state (between accepted and rejected)
        case 'injected':
            return 'final';

        // Rejected - unsuccessful final states
        case 'rejected':
        case 'user-rejected':
        case 'provider-rejected':
        // Failed/Cancelled states - treat as rejected
        case 'failed':
        case 'cancelled':
            return 'rejected';

        // Progress - in-progress/pending states (displayed as "Pending" in UI)
        case 'pending-action':
        case 'pending-request':
        case 'requested':
        case 'pending':
        case 'pendingcontinuous':
        case 'sent':
        case 'created':
            return 'progress';

        // Initial - just created
        case 'initial':
            return 'initial';

        default:
            // Unknown states fallback to lowest priority
            return 'unknown';
    }
}

/**
 * Get the sort priority for a result state.
 * Maps the state to its category, then looks up the category's priority.
 */
function getResultStatePriority(state: string): number {
    const category = getStateCategory(state);
    return resultStatePriorityMap[category] ?? -100;
}

/**
 * Get the last update time for a row.
 * Returns the most recent timestamp based on result state.
 */
function getLastUpdateTime(row: FlattenedLocateResultRow): Instant {
    // Return the most recent timestamp based on result state
    if (row.acceptedAt) return row.acceptedAt;
    if (row.rejectedAt) return row.rejectedAt;
    if (row.actionableAt) return row.actionableAt;
    return row.createdAt;
}

/**
 * Create an account comparator that sorts by account name.
 * If accounts list is not provided, falls back to comparing accountIds.
 */
export function createAccountComparator(accounts?: LocateAccount[]): FieldComparator {
    if (!accounts || accounts.length === 0) {
        // Fallback: compare by accountId
        return (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => {
            return (a.accountId ?? '').localeCompare(b.accountId ?? '');
        };
    }

    // Create a map for O(1) lookup
    const accountMap = new Map(accounts.map((acc) => [acc.id, acc.name] as [string, string]));

    return (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => {
        const aName = accountMap.get(a.accountId) ?? a.accountId ?? '';
        const bName = accountMap.get(b.accountId) ?? b.accountId ?? '';
        return aName.localeCompare(bName);
    };
}

/**
 * Field comparators for ascending sort order.
 * Use with `reverseComparator` for descending order.
 */
const fieldComparators = {
    account: createAccountComparator(), // Default fallback comparator
    borrowStatus: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) =>
        a.borrowStatus.localeCompare(b.borrowStatus),
    createdAt: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => a.createdAt.compareTo(b.createdAt),
    id: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => a.stableOrderId.localeCompare(b.stableOrderId),
    lastUpdateTime: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => {
        const aTime = getLastUpdateTime(a);
        const bTime = getLastUpdateTime(b);
        return aTime.compareTo(bTime);
    },
    price: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => {
        // Handle null values - treat null as less than any number (sorts to bottom)
        if (a.price === null && b.price === null) return 0;
        if (a.price === null) return -1;
        if (b.price === null) return 1;
        return a.price - b.price;
    },
    provider: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => a.providerId.localeCompare(b.providerId),
    quantity: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => {
        // orderQuantity is always a number, so simple numeric comparison
        return a.orderQuantity - b.orderQuantity;
    },
    resultState: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => {
        const aPriority = getResultStatePriority(a.resultState);
        const bPriority = getResultStatePriority(b.resultState);
        return aPriority - bPriority;
    },
    symbol: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => a.symbol.localeCompare(b.symbol),
    totalCost: (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => {
        // totalCost = price * offeredQuantity
        // Handle null values - treat null as less than any number (sorts to bottom)
        const aCost = a.price !== null && a.offeredQuantity !== null ? a.price * a.offeredQuantity : null;
        const bCost = b.price !== null && b.offeredQuantity !== null ? b.price * b.offeredQuantity : null;
        if (aCost === null && bCost === null) return 0;
        if (aCost === null) return -1;
        if (bCost === null) return 1;
        return aCost - bCost;
    },
};

/**
 * Reverses a comparator function to sort in descending order.
 */
function reverseComparator(comparator: FieldComparator): FieldComparator {
    return (a, b) => comparator(b, a);
}

/**
 * Composes multiple comparators into a single comparator.
 * Each comparator is tried in order until one returns a non-zero result.
 */
function composeComparators(...comparators: FieldComparator[]): FieldComparator {
    return (a, b) => {
        for (const comparator of comparators) {
            const result = comparator(a, b);
            if (result !== 0) {
                return result;
            }
        }
        return 0;
    };
}

/**
 * Sort configuration dictionary.
 * Maps sort names to their URL parameters and comparator functions.
 *
 * All sorts automatically include result_state desc as the first sort field to ensure
 * pending items always appear at the top, regardless of secondary sort direction.
 *
 * Usage:
 * - For fetching down: `sorts.createdAt.downSort` and `sorts.createdAt.downComparator`
 * - For fetching up: `sorts.createdAt.upSort` and `sorts.createdAt.upComparator`
 * - For URL params: `new URLSearchParams({ sort: sorts.createdAt.downSort })`
 */
export const sorts: SortDictionary = {
    /**
     * Account sort: always sorts by result_state desc first, then account name locally
     * Backend uses result_state desc, then createdAt for sorting (account name not supported by backend)
     */
    account: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.account),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.account,
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,createdAt asc,_id asc',
    },
    /**
     * BorrowStatus sort: always sorts by result_state desc first, then borrowStatus locally
     * Backend uses result_state desc, then createdAt for sorting (borrowStatus not supported by backend)
     */
    borrowStatus: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.borrowStatus),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.borrowStatus,
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,createdAt asc,_id asc',
    },
    /**
     * CreatedAt sort: always sorts by result_state desc first, then createdAt
     * This is the default sort behavior for the widget
     */
    createdAt: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,createdAt asc,_id asc',
    },
    /**
     * Last update time sort: always sorts by result_state desc first, then lastUpdateTime locally
     * Backend uses result_state desc, then createdAt for sorting (lastUpdateTime not supported by backend)
     */
    lastUpdateTime: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.lastUpdateTime),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.lastUpdateTime,
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,createdAt asc,_id asc',
    },
    /**
     * Price sort: always sorts by result_state desc first, then price locally
     * Backend uses result_state desc, then createdAt for sorting (price not supported by backend)
     */
    price: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.price),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.price,
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,createdAt asc,_id asc',
    },
    /**
     * Provider sort: always sorts by result_state desc first, then provider locally
     * Backend uses result_state desc, then createdAt for sorting (provider not supported by backend)
     */
    provider: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.provider),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.provider,
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,createdAt asc,_id asc',
    },
    /**
     * Quantity sort: always sorts by result_state desc first, then quantity locally
     * Backend uses result_state desc, then createdAt for sorting (quantity not supported by backend)
     */
    quantity: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.quantity),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.quantity,
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,createdAt asc,_id asc',
    },
    /**
     * Symbol sort: always sorts by result_state desc first, then symbol
     * Backend supports symbol sorting via locateRequest_symbol field
     */
    symbol: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.symbol),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,locateRequest_symbol desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.symbol,
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,locateRequest_symbol asc,createdAt asc,_id asc',
    },
    /**
     * TotalCost sort: always sorts by result_state desc first, then totalCost (price * offeredQuantity) locally
     * Backend uses result_state desc, then createdAt for sorting (totalCost not supported by backend)
     */
    totalCost: {
        downComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            reverseComparator(fieldComparators.totalCost),
            reverseComparator(fieldComparators.createdAt),
            reverseComparator(fieldComparators.id),
        ),
        downSort: 'result_state desc,createdAt desc,_id desc',
        upComparator: composeComparators(
            reverseComparator(fieldComparators.resultState),
            fieldComparators.totalCost,
            fieldComparators.createdAt,
            fieldComparators.id,
        ),
        upSort: 'result_state desc,createdAt asc,_id asc',
    },
};

/**
 * Get the comparator function for a given LocatesV2Sort value.
 * This is used by the RowDataSource to maintain local sort order.
 *
 * All comparators include result_state desc as the first sort field.
 *
 * @param sort - The sort configuration
 * @param accounts - Optional accounts list for account name sorting. If not provided, falls back to accountId sorting.
 */
export function getComparatorForSort(
    sort: LocatesV2Sort,
    accounts?: LocateAccount[],
): (a: FlattenedLocateResultRow, b: FlattenedLocateResultRow) => number {
    // Handle account sorts with dynamic comparator creation
    if (sort === 'account desc' || sort === 'account asc') {
        const accountComparator = createAccountComparator(accounts);
        const composedComparator = composeComparators(
            reverseComparator(fieldComparators.resultState),
            accountComparator,
            fieldComparators.createdAt,
            fieldComparators.id,
        );
        return sort === 'account desc' ? reverseComparator(composedComparator) : composedComparator;
    }

    switch (sort) {
        case 'createdAt desc':
            return sorts.createdAt.downComparator;
        case 'createdAt asc':
            return sorts.createdAt.upComparator;
        case 'symbol desc':
            return sorts.symbol.downComparator;
        case 'symbol asc':
            return sorts.symbol.upComparator;
        case 'lastUpdateTime desc':
            return sorts.lastUpdateTime.downComparator;
        case 'lastUpdateTime asc':
            return sorts.lastUpdateTime.upComparator;
        case 'price desc':
            return sorts.price.downComparator;
        case 'price asc':
            return sorts.price.upComparator;
        case 'provider desc':
            return sorts.provider.downComparator;
        case 'provider asc':
            return sorts.provider.upComparator;
        case 'quantity desc':
            return sorts.quantity.downComparator;
        case 'quantity asc':
            return sorts.quantity.upComparator;
        case 'borrowStatus desc':
            return sorts.borrowStatus.downComparator;
        case 'borrowStatus asc':
            return sorts.borrowStatus.upComparator;
        case 'totalCost desc':
            return sorts.totalCost.downComparator;
        case 'totalCost asc':
            return sorts.totalCost.upComparator;
    }
}
